import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { GamifyToast } from '../../../common/CustomToasts';

const stripePaymentProcessor = /* GraphQL */ `
  mutation StripePaymentProcessor($request: AWSJSON!) {
    stripePaymentProcessor(request: $request)
  }
`;

const maintainEstimateOrder = /* GraphQL */ `
  mutation MaintainEstimateOrder($request: AWSJSON!) {
    maintainEstimateOrder(request: $request)
  }
`;

interface EstimateOrder {
  id: string;
  dealID: string;
  orgID: string;
  userID: string;
  dealName?: string;
  dealAddress?: string;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  amount: number;
  currency: string;
  stripeCustomerID?: string;
  stripeUsageRecordID?: string;
  pdfUrl?: string;
  orderedAt: string;
  readyAt?: string;
}

interface DealEstimatesProps {
  dealID: string;
  dealInfo?: any;
}

const ESTIMATE_PRICE = 99.99;
const PROCESSING_DELAY_MS = 7000;

const DealEstimates: React.FC<DealEstimatesProps> = ({ dealID, dealInfo }) => {
  const { theme } = useTheme();
  const [orders, setOrders] = useState<EstimateOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // const [isOrdering, setIsOrdering] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!dealID) {
      setError('No deal ID provided');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request = JSON.stringify({
        operation: 'getEstimateOrdersByDeal',
        dealID,
      });

      const result: any = await API.graphql(
        graphqlOperation(maintainEstimateOrder, { request })
      );

      const responseData = result?.data?.maintainEstimateOrder;
      if (responseData) {
        let parsedResponse = responseData;
        while (typeof parsedResponse === 'string') {
          try {
            parsedResponse = JSON.parse(parsedResponse);
          } catch {
            break;
          }
        }

        if (parsedResponse.success && parsedResponse.data?.estimateOrders) {
          setOrders(parsedResponse.data.estimateOrders);
        } else if (parsedResponse.success) {
          setOrders([]);
        } else {
          throw new Error(
            parsedResponse.error || 'Failed to fetch estimate orders'
          );
        }
      } else {
        setOrders([]);
      }
    } catch (err: any) {
      console.error('Error fetching estimate orders:', err);
      setError(err.message || 'Failed to load estimate orders');
    } finally {
      setIsLoading(false);
    }
  }, [dealID]);

  useEffect(() => {
    fetchOrders();
    return () => {
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
      }
    };
  }, [fetchOrders]);

  // const handleOrderEstimate = () => {
  //   setShowConfirmModal(true);
  // };

  const handleConfirmOrder = async () => {
    setShowConfirmModal(false);
    // setIsOrdering(true);

    try {
      const request = JSON.stringify({
        operation: 'processEstimatePayment',
        dealID,
        dealName: dealInfo?.name || dealInfo?.customerName || '',
        dealAddress:
          dealInfo?.address ||
          dealInfo?.streetAddress ||
          dealInfo?.fullAddress ||
          '',
      });

      const result: any = await API.graphql(
        graphqlOperation(stripePaymentProcessor, { request })
      );

      const responseData = result?.data?.stripePaymentProcessor;
      if (responseData) {
        let parsedResponse = responseData;
        while (typeof parsedResponse === 'string') {
          try {
            parsedResponse = JSON.parse(parsedResponse);
          } catch {
            break;
          }
        }

        if (parsedResponse.success && parsedResponse.data?.estimateOrder) {
          const newOrder = parsedResponse.data.estimateOrder;
          setOrders((prev) => [newOrder, ...prev]);

          GamifyToast.success(
            'Estimate Ordered! Your Xactimate estimate is being processed...'
          );

          if (newOrder.status === 'PROCESSING') {
            processingTimerRef.current = setTimeout(async () => {
              await markOrderReady(newOrder.id);
            }, PROCESSING_DELAY_MS);
          }
        } else {
          throw new Error(
            parsedResponse.error || 'Failed to process estimate order'
          );
        }
      }
    } catch (err: any) {
      console.error('Error ordering estimate:', err);
      GamifyToast.error(
        err.message || 'Failed to order estimate. Please try again.'
      );
    } finally {
      // setIsOrdering(false);
    }
  };

  const markOrderReady = async (orderId: string) => {
    try {
      const request = JSON.stringify({
        operation: 'markOrderReady',
        orderID: orderId,
      });

      const result: any = await API.graphql(
        graphqlOperation(stripePaymentProcessor, { request })
      );

      const responseData = result?.data?.stripePaymentProcessor;
      if (responseData) {
        let parsedResponse = responseData;
        while (typeof parsedResponse === 'string') {
          try {
            parsedResponse = JSON.parse(parsedResponse);
          } catch {
            break;
          }
        }

        if (parsedResponse.success && parsedResponse.data?.estimateOrder) {
          const updatedOrder = parsedResponse.data.estimateOrder;
          setOrders((prev) =>
            prev.map((o) => (o.id === orderId ? updatedOrder : o))
          );
          GamifyToast.success(
            'Estimate Ready! Your Xactimate estimate is now available to view.'
          );
        }
      }
    } catch (err: any) {
      console.error('Error marking order as ready:', err);
    }
  };

  const handleViewOrder = (order: EstimateOrder) => {
    if (order.status === 'READY' && order.pdfUrl) {
      setPdfUrl(order.pdfUrl);
      setShowPDFViewer(true);
    } else if (order.status === 'PROCESSING') {
      GamifyToast.info(
        'Your estimate is being prepared. Please check back shortly.'
      );
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'READY':
        return '#4CAF50';
      case 'PROCESSING':
        return '#FF9800';
      case 'FAILED':
        return '#F44336';
      case 'PENDING':
      default:
        return theme.textLight;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'READY':
        return 'Ready to View';
      case 'PROCESSING':
        return 'Processing...';
      case 'FAILED':
        return 'Failed';
      case 'PENDING':
        return 'Pending';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'READY':
        return <CheckIcon sx={{ fontSize: 14 }} />;
      case 'PROCESSING':
        return <ClockIcon sx={{ fontSize: 14 }} />;
      case 'FAILED':
        return <CloseIcon sx={{ fontSize: 14 }} />;
      default:
        return <ClockIcon sx={{ fontSize: 14 }} />;
    }
  };

  const renderHeader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.text }}>
        Estimates
      </Typography>
    </Box>
  );

  const renderLoading = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
      }}
    >
      <CircularProgress size={32} sx={{ color: theme.primary }} />
      <Typography sx={{ mt: 2, color: theme.textLight, textAlign: 'center' }}>
        Loading estimates...
      </Typography>
    </Box>
  );

  const renderError = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
      }}
    >
      <CloseIcon sx={{ fontSize: 40, color: '#FF0000', mb: 2 }} />
      <Typography sx={{ color: theme.text, textAlign: 'center' }}>
        Error loading estimates
      </Typography>
      <Typography
        sx={{
          mt: 1,
          color: theme.textLight,
          textAlign: 'center',
          fontSize: 14,
        }}
      >
        {error}
      </Typography>
      <Button
        onClick={fetchOrders}
        variant="contained"
        sx={{
          mt: 2,
          backgroundColor: theme.primary,
          '&:hover': { backgroundColor: theme.primaryDark },
        }}
      >
        Retry
      </Button>
    </Box>
  );

  const renderEmpty = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
      }}
    >
      <FolderIcon sx={{ fontSize: 40, color: theme.textLight, mb: 2 }} />
      <Typography sx={{ color: theme.textLight, textAlign: 'center' }}>
        No estimates yet
      </Typography>
    </Box>
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    GamifyToast.success('Estimate ID copied to clipboard');
  };

  const renderOrderItem = (order: EstimateOrder, index: number) => (
    <Box
      key={order.id || index}
      onClick={() => handleViewOrder(order)}
      sx={{
        backgroundColor: theme.background2,
        borderRadius: 2,
        p: 2,
        mb: 1.5,
        borderLeft: `4px solid ${getStatusColor(order.status)}`,
        cursor: order.status === 'READY' ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        opacity: order.status === 'READY' ? 1 : 0.9,
        '&:hover':
          order.status === 'READY'
            ? {
                backgroundColor: theme.background3,
                transform: 'translateX(4px)',
              }
            : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{ fontWeight: 600, color: theme.text, mb: 0.5, fontSize: 16 }}
          >
            Xactimate Estimate
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.textLight }}>
            Ordered {formatDate(order.orderedAt)}
          </Typography>
        </Box>
        <Chip
          icon={
            order.status === 'PROCESSING' ? (
              <CircularProgress
                size={12}
                sx={{ color: getStatusColor(order.status) }}
              />
            ) : (
              getStatusIcon(order.status)
            )
          }
          label={getStatusLabel(order.status)}
          size="small"
          sx={{
            backgroundColor: `${getStatusColor(order.status)}20`,
            color: getStatusColor(order.status),
            fontWeight: 600,
            fontSize: 12,
            '& .MuiChip-icon': {
              color: getStatusColor(order.status),
            },
          }}
        />
      </Box>

      {/* Estimate ID Section */}
      <Box
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: `1px solid ${theme.stroke}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{ fontSize: 11, color: theme.textLight, fontWeight: 500 }}
            >
              ESTIMATE ID
            </Typography>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(order.id);
              }}
              sx={{
                backgroundColor: theme.background3,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                cursor: 'pointer',
                border: `1px solid ${theme.stroke}`,
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: theme.background1,
                  borderColor: theme.primary,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: theme.text,
                  letterSpacing: '0.5px',
                }}
              >
                {order.id}
              </Typography>
            </Box>
          </Box>
          {order.status === 'READY' && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.primary,
                  mr: 0.5,
                }}
              >
                View
              </Typography>
              <ChevronRightIcon sx={{ color: theme.primary, fontSize: 20 }} />
            </Box>
          )}
        </Box>
      </Box>

      {order.status === 'PROCESSING' && (
        <Typography
          sx={{
            fontSize: 11,
            color: theme.textLight,
            mt: 1,
            fontStyle: 'italic',
          }}
        >
          Your estimate is being prepared. This usually takes a few hours.
        </Typography>
      )}
    </Box>
  );

  const renderOrdersList = () => (
    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
      {orders.map((order, index) => renderOrderItem(order, index))}
    </Box>
  );

  const renderConfirmModal = () => (
    <Dialog
      open={showConfirmModal}
      onClose={() => setShowConfirmModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.background1,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: theme.text, textAlign: 'center', pt: 4 }}>
        Order Xactimate Estimate
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontSize: 36,
            fontWeight: 700,
            color: theme.primary,
            my: 2,
          }}
        >
          {formatCurrency(ESTIMATE_PRICE)}
        </Typography>
        <Typography sx={{ color: theme.textLight, mb: 3 }}>
          A professional Xactimate insurance claim estimate will be prepared for
          this property. Orders are typically processed within a few hours.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1.5 }}>
        <Button
          onClick={() => setShowConfirmModal(false)}
          sx={{
            flex: 1,
            py: 1.5,
            backgroundColor: theme.background2,
            color: theme.text,
            '&:hover': { backgroundColor: theme.background3 },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmOrder}
          variant="contained"
          sx={{
            flex: 1,
            py: 1.5,
            backgroundColor: theme.primary,
            '&:hover': { backgroundColor: theme.primaryDark },
          }}
        >
          Confirm Purchase
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPDFViewer = () => (
    <Dialog
      open={showPDFViewer}
      onClose={() => setShowPDFViewer(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogContent
        sx={{ padding: 0, overflow: 'hidden', position: 'relative' }}
      >
        <IconButton
          onClick={() => setShowPDFViewer(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            zIndex: 1000,
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '85vh' }}
            title="Xactimate Estimate"
          />
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ p: 2 }}>
      {renderHeader()}
      {isLoading && renderLoading()}
      {error && !isLoading && renderError()}
      {!isLoading && !error && orders.length === 0 && renderEmpty()}
      {!isLoading && !error && orders.length > 0 && renderOrdersList()}
      {renderConfirmModal()}
      {renderPDFViewer()}
    </Box>
  );
};

export default DealEstimates;
